import debug from 'debug';
import {
  Criterion,
  ImpactToolInput,
  ImpactToolResult,
} from '~/types/impactTool';

const log = debug('fmc:ImpactToolStorageService');

const impactToolDataKey = 'impactToolData';
const impactToolResultKey = 'impactToolResult';
const impactToolCriterions = 'impactToolCriterions';
const prefix = 'fmc.';

export const readAndParseImpactToolData = (): ImpactToolInput => {
  try {
    const dataString =
      typeof window !== 'undefined' &&
      window.localStorage.getItem(prefix + impactToolDataKey);
    const data = JSON.parse(dataString || '');
    log(data);
    return data;
  } catch (e) {
    throw Error('Could not read Impact Tool Data');
  }
};

export const writeImpactToolData = (data: ImpactToolInput): boolean => {
  try {
    window.localStorage.setItem(
      prefix + impactToolDataKey,
      JSON.stringify(data)
    );
    return true;
  } catch (e) {
    throw Error('Could not save impact tool data');
  }
};

export const localStorageHasImpactData = (): boolean => {
  const data = window.localStorage.getItem(prefix + impactToolDataKey);
  if (!!data && data !== null) {
    return true;
  }
  return false;
};

export const localStorageHasCriterions = (): boolean => {
  const data = window.localStorage.getItem(prefix + impactToolCriterions);
  if (!!data && data !== null) {
    return true;
  }
  return false;
};

export const parseDataFromUrl = (
  location: string
): { result: ImpactToolResult; criterions: Array<Criterion> } | void => {
  try {
    const params = new URLSearchParams(location);
    const resultData = params.get(impactToolDataKey);
    const criterionData = params.get(impactToolCriterions);
    if (resultData && criterionData) {
      return {
        result: JSON.parse(resultData),
        criterions: JSON.parse(criterionData),
      };
    }
  } catch (e) {
    throw Error('Could not read impact tool data from url');
  }
};

export const generateDataUrl = (
  url: URL | string,
  data: ImpactToolResult,
  criterions?: Array<Criterion>
): URL => {
  try {
    if (typeof url === 'string') {
      url = new URL(url);
    }
    if (url.searchParams.has('origin')) {
      url.searchParams.set('origin', 'sharelink');
    } else {
      url.searchParams.append('origin', 'sharelink');
    }

    if (url.searchParams.has(impactToolDataKey)) {
      url.searchParams.set(impactToolDataKey, JSON.stringify(data));
    } else {
      url.searchParams.append(impactToolDataKey, JSON.stringify(data));
    }

    if (criterions) {
      if (url.searchParams.has(impactToolCriterions)) {
        url.searchParams.set(
          impactToolCriterions,
          JSON.stringify(trimCriterionList(criterions))
        );
      } else {
        url.searchParams.append(
          impactToolCriterions,
          JSON.stringify(trimCriterionList(criterions))
        );
      }
    }
    return url;
  } catch (e) {
    throw Error('Could not generate url with data');
  }
};

export const urlHasResult = (url: string): boolean => {
  return new URLSearchParams(url).has(impactToolDataKey);
};

export const writeResult = (result: ImpactToolResult | void): boolean => {
  try {
    window.localStorage.setItem(
      prefix + impactToolResultKey,
      JSON.stringify(result)
    );
    return true;
  } catch (e) {
    throw Error('Could not save impact tool result');
  }
};

export const writeCriterions = (criterions: Array<Criterion>): boolean => {
  try {
    criterions = trimCriterionList(criterions);
    window.localStorage.setItem(
      prefix + impactToolCriterions,
      JSON.stringify(criterions)
    );
    return true;
  } catch (e) {
    throw Error('Could not save impact tool criterions');
  }
};

export const localStorageHasImpactResult = (): boolean => {
  const data = window.localStorage.getItem(prefix + impactToolResultKey);
  if (!!data && data !== null) {
    return true;
  }
  return false;
};

export const readAndParseImpactToolResult = (): ImpactToolResult => {
  try {
    const resultString =
      typeof window !== 'undefined' &&
      window.localStorage.getItem(prefix + impactToolResultKey);
    const result = JSON.parse(resultString || '');
    return result;
  } catch (e) {
    throw new Error('Could not read impact tool result from localStorage');
  }
};

export const readAndParseImpactToolCriterions = (): Array<Criterion> => {
  try {
    const criterionString =
      typeof window !== 'undefined' &&
      window.localStorage.getItem(prefix + impactToolCriterions);
    const criterions = JSON.parse(criterionString || '');
    return criterions;
  } catch (e) {
    throw new Error('Could not read impact tool criterions from localStorage');
  }
};

export const trimCriterionList = (
  criterions: Array<Criterion>
): Array<Criterion> => {
  return criterions.filter((c) => {
    if (
      !(c.criterion === '' && c.description === '' && c.fullfilled === false)
    ) {
      return c;
    }
  });
};
